<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue'
import { useRouter } from 'vue-router'

// Components
import NavBar from '../shared/components/common/NavBar.vue'

// Services
import { FirebaseStorageService } from '../shared/services/FirebaseStorageService'
import { ContentLibraryService } from '../shared/services/ContentLibraryService'
import { RequestService } from '../shared/services/RequestService'

// Utils
import { handleErrors } from '../shared/utils/errorHandler'
import { truncateText } from '../shared/utils'
import { normalizeContentLibraryEmbedUrl, sanitizeContentLibraryFileName } from '../shared/utils/contentLibraryHelpers'

// Constants
import { FILE_SIZE_LIMITS, ALLOWED_DOCUMENT_EXTENSIONS, ALLOWED_EXTENSIONS_LABEL } from '../shared/constants/fileTypes'
import { CONTENT_LIBRARY_ITEM_FILTER_DEFINITIONS } from '../shared/config/contentLibraryItemFilters'

// Branding tag detection
import { detectBrandingTags, isBrandingTagDetectable } from '../shared/services/brandingTagDetector'
import { isBrandableFileName, SUPPORTED_BRANDING_FORMATS_LABEL } from '../shared/constants/brandableFormats'

// Content Library template service
import { ContentLibraryTemplateService } from '../shared/services/ContentLibraryTemplateService'
import ContentLibraryAgencyPrerenderTab from '../shared/components/ContentLibraryAgencyPrerenderTab/ContentLibraryAgencyPrerenderTab.vue'

// Stores & Composables
import { useLogout } from '../shared/composables/useLogout'
import { useAgencyBranding } from '../shared/composables/useAgencyBranding'
import { useAuthStore } from '../shared/stores/AuthStore'
import { PERMISSION_KEYS } from '../shared/access/permissionKeys'

// Assets
import brandLogo from '@/assets/images/BCS-Branding.svg'

const router = useRouter()
const authStore = useAuthStore()
const activeTab = ref(0)
const showAddItemModal = ref(false)
const showAddCategoryModal = ref(false)
const isEditMode = ref(false)
const isCategoryEditMode = ref(false)
const editingItemId = ref(null)
const editingCategoryId = ref(null)
const showDeleteConfirm = ref(false)
const showRegenerateConfirm = ref(false)
const itemToRegenerate = ref(null)
const showInspectorModal = ref(false)
const inspectorTargetItem = ref(null)
const inspectorLoading = ref(false)
const inspectorData = ref(null)
const regeneratingAgencyId = ref(null)
const isRegeneratingAgencyCache = computed(() => Boolean(regeneratingAgencyId.value))
const showCategoryDeleteConfirm = ref(false)
const showDescriptionModal = ref(false)
const selectedDescription = ref('')
const selectedDescriptionItem = ref({ number: null, libraryFileName: '' })
const categoryToDelete = ref(null)

// Services
const contentLibraryService = ContentLibraryService()
const contentLibraryTemplateService = ContentLibraryTemplateService()
const { agencyId, logoSize, whitelabelColor, loadBranding } = useAgencyBranding()
const { createLog } = RequestService()
const categoryWarningMessage = ref('')
const itemToDelete = ref(null)
const isSavingItem = ref(false)
const notification = ref({ show: false, message: '', type: 'success' })
const isSavingCategory = ref(false)
const showBackToTop = ref(false)

const getFilterOptionValues = (key) => {
  return CONTENT_LIBRARY_ITEM_FILTER_DEFINITIONS
    .find((definition) => definition.key === key)
    ?.options.map((option) => option.value) || []
}

const lawScopeOptions = getFilterOptionValues('lawScope')
const recipientOptions = getFilterOptionValues('recipient')

// Helper function to determine file type from file name
const getFileTypeFromName = (fileName) => {
  if (!fileName) return null

  if (fileName.endsWith('.pdf')) return 'PDF'
  if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx') || fileName.endsWith('.ods')) return 'XLS'
  if (fileName.endsWith('.doc') || fileName.endsWith('.docx') || fileName.endsWith('.rtf')) return 'DOC'
  if (fileName.endsWith('.ppt') || fileName.endsWith('.pptx') || fileName.endsWith('.ppsx') || fileName.endsWith('.pptm') || fileName.endsWith('.odp')) return 'PPT'
  
  return null // No file type if extension not recognized
}

const isExcelFile = (fileName) => {
  const ext = getFileExtensionFromName(fileName)
  return ext === 'xls' || ext === 'xlsx' || ext === 'ods'
}

const isWordFile = (fileName) => {
  const ext = getFileExtensionFromName(fileName)
  return ext === 'doc' || ext === 'docx' || ext === 'rtf'
}

const isPowerPointFile = (fileName) => {
  const ext = getFileExtensionFromName(fileName)
  return ext === 'ppt' || ext === 'pptx' || ext === 'ppsx' || ext === 'pptm' || ext === 'odp'
}


const { getImageUrl } = FirebaseStorageService()
const uid = localStorage.getItem('userUid')

// Loading counter system
const loadingCount = ref(0)
const loading = computed(() => loadingCount.value > 0)

// Reactive data
const userData = ref({
  profilePhotoUrl: null,
  temporaryPhotoURL: null
})

const addItemForm = ref({
  libraryFileName: '',
  libraryCategoryId: '',
  libraryCategoryName: '',
  description: '',
  lawScope: '',
  recipient: '',
  documentFileName: '',
  documentDisplayName: '',
  zoomUrl: '',
  zoomPassword: '',
  embedUrl: '',
  documentFile: null
})

// File upload variables
const fileInput = ref(null)
const uploadedFileName = ref('')
const isDragOver = ref(false)

// Branding template detection state
const brandingDetection = ref({
  state: 'idle', // idle | analyzing | detected | uncertain | not_detected | unsupported_format | error
  markers: [],
  reason: ''
})
let detectionFileId = 0 // used to ignore stale detection results

// Branding template preview / unsupported-format modal
const showBrandingPreviewModal = ref(false)
const brandingModalMode = ref('preview') // preview | unsupported_format
const previewPdfUrl = ref(null)
const brandingUploadStep = ref('idle') // idle | previewing | ready | error
const brandingUploadError = ref('')
const pendingBrandingEngine = ref(null)
const pendingBrandingUploadedAt = ref(null)
const brandingReviewBlob = ref(null)
const previewFileFormat = ref('')

// File tracking for edit mode
const originalFileData = ref({
  hasFile: false,
  fileName: '',
  displayName: ''
})

// Original field values for change-detection on edit (drives updateDescription)
const originalItemData = ref({})

// Add Category Form Data
const addCategoryForm = ref({
  name: '',
  description: '',
  taxonomyGroupId: ''
})

const tableData = ref([])
const tableSearchQuery = ref('')
const filteredTableData = computed(() => {
  const term = String(tableSearchQuery.value || '').trim().toLowerCase()
  if (!term) return tableData.value

  return tableData.value.filter((item) => {
    const libraryFileName = String(item?.libraryFileName || '').toLowerCase()
    const documentFileName = String(item?.documentFileName || '').toLowerCase()
    return libraryFileName.includes(term) || documentFileName.includes(term)
  })
})
const categories = ref([])
const taxonomyGroups = ref([])

const isAdmin = ref(false)
const tabNames = computed(() => {
  const tabs = ['Table', 'Categories']
  if (isAdmin.value) {
    tabs.push('Agency pre-render')
  }
  return tabs
})
const agencyPrerenderTabIndex = computed(() => tabNames.value.indexOf('Agency pre-render'))
const showPrimaryAddButton = computed(() => activeTab.value === 0 || activeTab.value === 1)
const profileItems = [
  { 
    id: 'profile', 
    label: 'Profile', 
    target: 'profile', 
    type: 'dropdown', 
    showMobile: true 
  },
  { 
    id: 'logout', 
    label: 'Log Out', 
    target: 'logout', 
    type: 'dropdown', 
    showMobile: true 
  }
]

// Lifecycle hooks
onMounted(async () => {
  try {
    await authStore.ensureAccessLoaded()
    isAdmin.value = authStore.hasPermission(PERMISSION_KEYS.CONTENT_MANAGER_ACCESS)
  } catch {
    isAdmin.value = false
  }
  await loadProfilePhoto()
  await loadContentItems()
  startCacheStatusSubscription()
  await loadCategories()
  await loadTaxonomyGroups()
  loadBranding() // fire-and-forget, branding is optional
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  stopCacheStatusSubscription()
})

const handleScroll = () => {
  showBackToTop.value = window.scrollY > 300
}

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function pushLoading() {
  loadingCount.value++
}
 
function popLoading() {
  if (loadingCount.value > 0) {
    loadingCount.value--
  }
}

// Methods
const loadProfilePhoto = async () => {
  pushLoading()
  try {
    const photoURL = await getImageUrl('profile-photos', `${uid}.png`)
    if (photoURL) {
      userData.value.profilePhotoUrl = photoURL
    }
  } catch (error) {
    handleErrors('ContentLibrary.loadProfilePhoto', error)
  } finally {
    popLoading()
  }
}

const loadTaxonomyGroups = async () => {
  pushLoading()
  try {
    const groups = await contentLibraryService.getTaxonomyGroups({ activeOnly: true })
    taxonomyGroups.value = groups
  } catch (error) {
    handleErrors('ContentLibrary.loadTaxonomyGroups', error)
    taxonomyGroups.value = []
  } finally {
    popLoading()
  }
}

const loadContentItems = async () => {
  pushLoading()
  try {
    const items = await contentLibraryService.getItems()
    tableData.value = items.map(item => ({
      id: item.id,
      libraryFileName: item.libraryFileName,
      libraryCategoryId: item.libraryCategoryId,
      libraryCategoryName: item.libraryCategoryName,
      description: item.description,
      lawScope: item.lawScope,
      recipient: item.recipient,
      documentFileName: item.documentFileName,
      documentDisplayName: item.documentDisplayName,
      zoomUrl: item.zoomUrl,
      zoomPassword: item.zoomPassword,
      embedUrl: item.embedUrl,
      fileType: getFileTypeFromName(item.documentFileName),
      createdAt: item.created_at,
      brandingEngine: item.brandingEngine || null,
      cacheStatus: item.cacheStatus,
      cacheError: item.cacheError,
      cacheUpdatedAt: item.cacheUpdatedAt
    }))
  } catch (error) {
    handleErrors('ContentLibrary.loadContentItems', error)
  } finally {
    popLoading()
  }
}

// Realtime cache-status subscription — keeps cacheStatus / cacheError /
// cacheUpdatedAt in sync via RTDB onValue. Replaces the previous polling so
// the badge transitions arrive push-style with no extra HTTP traffic.
//
// Scope is intentionally narrow: only the three cache fields are merged into
// existing `tableData` rows. Inserts and deletes still flow through the
// explicit `loadContentItems()` calls after create/update/delete to preserve
// ordering, filters and pushLoading UX.
let unsubscribeCacheStatus = null

const hasTemplateIdentifier = (item) => Boolean(item?.brandingEngine)

// Pending = 'pending' (seeded on write or queued by manual regen) or
// 'processing' (trigger picked it up). Undefined intentionally excluded:
// legacy items predating this feature have no cacheStatus field and must not
// spin forever — they will be backfilled by a maintenance job.
const isItemCachePending = (item) => (
  hasTemplateIdentifier(item) &&
  (item?.cacheStatus === 'pending' || item?.cacheStatus === 'processing')
)

const startCacheStatusSubscription = () => {
  if (unsubscribeCacheStatus) {
    return
  }
  unsubscribeCacheStatus = contentLibraryService.subscribeToItems((items) => {
    const cacheById = new Map(items.map((item) => [item.id, item]))
    for (const row of tableData.value) {
      const fresh = cacheById.get(row.id)
      if (!fresh) continue
      row.cacheStatus = fresh.cacheStatus
      row.cacheError = fresh.cacheError
      row.cacheUpdatedAt = fresh.cacheUpdatedAt
    }
  })
}

const stopCacheStatusSubscription = () => {
  if (unsubscribeCacheStatus) {
    unsubscribeCacheStatus()
    unsubscribeCacheStatus = null
  }
}

const loadCategories = async () => {
  pushLoading()
  try {
    const cats = await contentLibraryService.getCategories()
    categories.value = cats
  } catch (error) {
    handleErrors('ContentLibrary.loadCategories', error)
  } finally {
    popLoading()
  }
}

const { logout: performLogout } = useLogout()

const handleLogout = async () => {
  await performLogout()
}

const handleLogoClick = () => {
  router.push('/home')
}

const handleProfileClick = (item) => {
  if (item.id === 'logout') {
    handleLogout()
  } else if (item.id === 'profile') {
    // Set flag to navigate to profile after redirect
    sessionStorage.setItem('navigateToProfile', 'true')
    // Navigate to home page where profile component is available
    router.push('/home')
  }
}

const goBack = () => {
  router.push('/home')
}

// Add Item Modal Functions
const openAddItemModal = () => {
  isEditMode.value = false
  editingItemId.value = null
  resetAddItemForm()
  showAddItemModal.value = true
}

const closeAddItemModal = () => {
  showAddItemModal.value = false
  resetAddItemForm()
  isEditMode.value = false
  editingItemId.value = null
}

const resetAddItemForm = () => {
  addItemForm.value = {
    libraryFileName: '',
    libraryCategoryId: '',
    libraryCategoryName: '',
    description: '',
    lawScope: '',
    recipient: '',
    documentFileName: '',
    documentDisplayName: '',
    zoomUrl: '',
    zoomPassword: '',
    embedUrl: '',
    documentFile: null
  }
  uploadedFileName.value = ''
  
  // Reset file tracking data
  originalFileData.value = {
    hasFile: false,
    fileName: '',
    displayName: ''
  }
  originalItemData.value = {}

  // Reset detection and upload state
  detectionFileId++
  brandingDetection.value = { state: 'idle', markers: [], reason: '' }
  resetBrandingUploadState()
}

const saveAddItem = async () => {
  // Prevent saving if form is empty
  if (isFormEmpty.value) {
    return
  }
  
  if (uploadedFileName.value) {
    const collidingItem = findCollidingItem(uploadedFileName.value)
    if (collidingItem) {
      showNotification(`Another item already uses a file named "${collidingItem.documentFileName}". Rename your file and try again.`, 'error')
      return
    }
  }

  if (brandingDetection.value.state === 'unsupported_format') {
    showNotification(
      `Branding template tags were detected, but only ${SUPPORTED_BRANDING_FORMATS_LABEL} files can be saved with automated branding. Remove the file or upload a supported template.`,
      'error'
    )
    return
  }

  isSavingItem.value = true
  try {
    const hasBrandingTemplate = !!pendingBrandingEngine.value

    if (hasBrandingTemplate && !addItemForm.value.documentFile) {
      showNotification('Template file is missing. Please upload the file again.', 'error')
      return
    }

    let itemData = {
      libraryFileName: addItemForm.value.libraryFileName || null,
      libraryCategoryId: addItemForm.value.libraryCategoryId || null,
      libraryCategoryName: addItemForm.value.libraryCategoryName || null,
      description: addItemForm.value.description || null,
      lawScope: addItemForm.value.lawScope || null,
      recipient: addItemForm.value.recipient || null,
      documentFileName: uploadedFileName.value || null,
      documentDisplayName: addItemForm.value.documentDisplayName || null,
      zoomUrl: addItemForm.value.zoomUrl || null,
      zoomPassword: addItemForm.value.zoomPassword || null,
      embedUrl: normalizeContentLibraryEmbedUrl(addItemForm.value.embedUrl) || null,
      // Brandable templates: preview renders in memory only. Upload the
      // original to Storage when the item is saved.
      ...(hasBrandingTemplate
        ? {
            brandingEngine: pendingBrandingEngine.value,
            documentFile: addItemForm.value.documentFile
          }
        : { documentFile: addItemForm.value.documentFile || null })
    }
    
    let targetItemId = null
    
    if (isEditMode.value && editingItemId.value) {
      // Determine if file should be deleted: original had file but current doesn't
      const shouldDeleteFile = originalFileData.value.hasFile && !uploadedFileName.value

      // Detect which tracked fields changed and build updateDescription
      const orig = originalItemData.value
      const curr = addItemForm.value
      const changedDescriptions = []
      if (shouldDeleteFile || curr.documentFile) {
        changedDescriptions.push('Document File Updated')
      }
      if ((curr.description || '') !== (orig.description || '')) {
        changedDescriptions.push('Description Updated')
      }
      if (changedDescriptions.length > 0) {
        itemData.updateDescription = changedDescriptions.join(', ')
      }
      
      await contentLibraryService.updateItem(editingItemId.value, itemData, shouldDeleteFile)

      // Fire-and-forget: send Content Library update log when tracked fields changed
      if (changedDescriptions.length > 0) {
        createLog({
          logName: 'content_library_activity_log_v1',
          eventId: 'CL_UPD',
          agencyId: agencyId.value ? String(agencyId.value) : null,
          body: { itemId: editingItemId.value, updateDescription: changedDescriptions.join(', ') }
        }).catch(err => handleErrors('createLog CL update', err))
      }

      targetItemId = editingItemId.value
      await loadContentItems()
      
      // Show success notification for edit
      showNotification('Item updated successfully')
    } else {
      const result = await contentLibraryService.createItem(itemData)
      // If backend returned null (no data saved), don't reload or close modal
      if (result === null) {
        return
      }
      
      targetItemId = result.id
      await loadContentItems()
      
      // Show success notification for create
      showNotification('Item created successfully')
    }
    
    // Template was persisted — clear pending so reset won't delete it
    pendingBrandingEngine.value = null
    pendingBrandingUploadedAt.value = null

    closeAddItemModal()
    
    // Scroll to and highlight the target item
    if (targetItemId) {
      await nextTick()
      highlightAndScrollToRow(targetItemId)
    }
  } catch (error) {
    handleErrors('ContentLibrary.saveAddItem', error)
    showNotification('Error saving item', 'error')
  } finally {
    isSavingItem.value = false
  }
}

const showNotification = (message, type = 'success') => {
  notification.value = { show: true, message, type }
  setTimeout(() => {
    notification.value = { show: false, message: '', type: 'success' }
  }, 4000)
}

const handleAgencyPrerenderNotify = (message, type = 'success') => {
  showNotification(message, type)
}

const highlightAndScrollToRow = (itemId) => {
  const element = document.getElementById(`contentLibraryRow-${itemId}`)
  if (!element) return

  element.scrollIntoView({ behavior: 'smooth', block: 'center' })
  
  // Phase 1: Strong purple + bold
  element.classList.add('highlight-row')
  
  // Phase 2: After 3 seconds, softer purple + normal weight
  setTimeout(() => {
    element.classList.add('highlight-soft')
  }, 3000)
  
  // Phase 3: After 6 seconds, fade out
  setTimeout(() => {
    element.classList.add('highlight-fade')
    
    // Clean up classes after transition completes
    setTimeout(() => {
      element.classList.remove('highlight-row', 'highlight-soft', 'highlight-fade')
    }, 1000)
  }, 6000)
}

const highlightAndScrollToCategory = (categoryId) => {
  const element = document.getElementById(`categoryItem-${categoryId}`)
  if (!element) return

  element.scrollIntoView({ behavior: 'smooth', block: 'center' })
  
  // Phase 1: Strong purple + bold
  element.classList.add('highlight-row')
  
  // Phase 2: After 3 seconds, softer purple + normal weight
  setTimeout(() => {
    element.classList.add('highlight-soft')
  }, 3000)
  
  // Phase 3: After 6 seconds, fade out
  setTimeout(() => {
    element.classList.add('highlight-fade')
    
    // Clean up classes after transition completes
    setTimeout(() => {
      element.classList.remove('highlight-row', 'highlight-soft', 'highlight-fade')
    }, 1000)
  }, 6000)
}

// File handling functions
const openFilePicker = () => fileInput.value?.click()
const onInputChange = (event) => handleFileSelection(event.target.files[0])
const onDrop = (event) => handleFileSelection(event.dataTransfer.files[0])

// Detects duplicate uploads BEFORE writing to RTDB so two items cannot resolve
// to the same Storage object. Derives the collision key from the same shared
// helper used by uploads and downloads, so any two filenames that produce the
// same Storage path are treated as the same document. Lowercased on top so
// case-only variants (Plan-Guide.docx vs plan-guide.docx) are also caught.
const computeDocumentFileNameSlug = (documentFileName) => {
  const { cleanName } = sanitizeContentLibraryFileName(documentFileName)
  return cleanName.toLowerCase()
}

const findCollidingItem = (candidateFileName) => {
  const candidateSlug = computeDocumentFileNameSlug(candidateFileName)
  if (!candidateSlug) return null
  return tableData.value.find((existing) => {
    if (!existing?.documentFileName) return false
    if (isEditMode.value && existing.id === editingItemId.value) return false
    return computeDocumentFileNameSlug(existing.documentFileName) === candidateSlug
  }) || null
}

const handleFileSelection = async (file) => {
  if (!file) return

  if (file.name?.startsWith('~$')) {
    showNotification('This looks like a temporary Microsoft Office lock file. Please upload the original document instead of the ~$ file.', 'error')
    return
  }
  
  // Validations using constants
  const fileNameSegments = String(file.name || '').split('.')
  const originalFileExtension = fileNameSegments.length > 1 ? fileNameSegments.pop() : ''
  const fileExtension = originalFileExtension.toLowerCase()
  if (!ALLOWED_DOCUMENT_EXTENSIONS.has(fileExtension)) {
    showNotification(`Only document files are allowed (${ALLOWED_EXTENSIONS_LABEL})`, 'error')
    return
  }
  
  if (file.size > FILE_SIZE_LIMITS.MAX_DOCUMENT_SIZE) {
    showNotification('File size must be less than 10MB', 'error')
    return
  }

  const collidingItem = findCollidingItem(file.name)
  if (collidingItem) {
    showNotification(`Another item already uses a file named "${collidingItem.documentFileName}". Rename your file and try again.`, 'error')
    return
  }

  // Assign file and name without timestamp
  const cleanName = file.name.replace(/\.[^/.]+$/, '') // Remove extension
  
  addItemForm.value.documentFile = file
  addItemForm.value.documentFileName = `${cleanName}.${originalFileExtension}` // Remove timestamp
  uploadedFileName.value = file.name
  
  // Smart logic for documentDisplayName
  if (!addItemForm.value.documentDisplayName?.trim() || 
      addItemForm.value.documentDisplayName === originalFileData.value.fileName) {
    // If empty or equal to previous name, use new name
    addItemForm.value.documentDisplayName = cleanName
  }
  // If user wrote a custom name, keep it intact

  // Run branding tag detection for Word/PowerPoint files
  if (isBrandingTagDetectable(file.name)) {
    const currentFileId = ++detectionFileId
    brandingDetection.value = { state: 'analyzing', markers: [], reason: '' }

    try {
      const result = await detectBrandingTags(file)

      // Ignore stale results if user replaced or removed the file
      if (currentFileId !== detectionFileId) return

      brandingDetection.value = result

      if (result.state === 'detected' || result.state === 'uncertain') {
        if (isBrandableFileName(file.name)) {
          await startBrandingPreviewFlow(file, currentFileId)
        } else {
          openUnsupportedBrandingFormatModal(result)
        }
      }
    } catch (err) {
      if (currentFileId !== detectionFileId) return
      brandingDetection.value = {
        state: 'error',
        markers: [],
        reason: err?.message || 'Detection failed unexpectedly.'
      }
    }
  } else {
    // Extensions outside the detector (e.g. PDF) → no tag scan, direct upload on Save
    brandingDetection.value = { state: 'idle', markers: [], reason: '' }
  }
}

const openUnsupportedBrandingFormatModal = (detectionResult) => {
  resetBrandingUploadState()
  brandingModalMode.value = 'unsupported_format'
  brandingDetection.value = {
    state: 'unsupported_format',
    markers: detectionResult.markers || [],
    reason: detectionResult.reason || ''
  }
  showBrandingPreviewModal.value = true
}

/**
 * Send template to backend for in-memory branded preview (no Storage until Save).
 */
const startBrandingPreviewFlow = async (file, fileId) => {
  resetBrandingUploadState()
  brandingModalMode.value = 'preview'
  brandingUploadStep.value = 'previewing'
  showBrandingPreviewModal.value = true

  try {
    const { brandingEngine, documentFileName, reviewBlob, reviewFileFormat } = await contentLibraryTemplateService.previewTemplate(file, {
      agencyId: agencyId.value,
      logoSize: logoSize.value,
      whitelabelColor: whitelabelColor.value
    })

    // Ignore if user replaced or removed the file while upload was in-flight
    if (fileId !== detectionFileId) {
      return
    }

    pendingBrandingEngine.value = brandingEngine
    uploadedFileName.value = documentFileName
    pendingBrandingUploadedAt.value = Date.now()
    brandingReviewBlob.value = reviewBlob
    previewFileFormat.value = String(reviewFileFormat || '').toLowerCase()
    brandingUploadStep.value = 'ready'
  } catch (err) {
    if (fileId !== detectionFileId) return

    brandingUploadStep.value = 'error'
    brandingUploadError.value = err?.message || 'Template preview failed. Please try again.'
    handleErrors('ContentLibrary.startBrandingPreviewFlow', err)
  }
}

const resetBrandingUploadState = () => {
  brandingModalMode.value = 'preview'
  brandingUploadStep.value = 'idle'
  brandingUploadError.value = ''
  pendingBrandingEngine.value = null
  pendingBrandingUploadedAt.value = null
  brandingReviewBlob.value = null
  previewFileFormat.value = ''
  if (previewPdfUrl.value?.startsWith('blob:')) {
    URL.revokeObjectURL(previewPdfUrl.value)
  }
  previewPdfUrl.value = null
}

const removeFile = () => {
  resetBrandingUploadState()

  addItemForm.value.documentFile = null
  addItemForm.value.documentFileName = ''
  addItemForm.value.documentDisplayName = ''
  uploadedFileName.value = ''
  if (fileInput.value) fileInput.value.value = ''
  // Reset detection state and invalidate any in-flight detection
  detectionFileId++
  brandingDetection.value = { state: 'idle', markers: [], reason: '' }
}

// Branding preview modal functions
// The preview file extension matches the source extension for .xlsx/.pptx
// uploads and falls back to .pdf for .docx.
const REVIEW_EXTENSIONS_BY_SOURCE = { xlsx: 'xlsx', pptx: 'pptx' }
const sourceFileExtension = computed(() => {
  const name = uploadedFileName.value || ''
  const dotIndex = name.lastIndexOf('.')
  return dotIndex >= 0 ? name.slice(dotIndex + 1).toLowerCase() : ''
})
const reviewFileExtension = computed(() => {
  if (previewFileFormat.value) {
    return previewFileFormat.value
  }
  return REVIEW_EXTENSIONS_BY_SOURCE[sourceFileExtension.value] || 'pdf'
})
const reviewFileButtonLabel = computed(() => `Download review ${reviewFileExtension.value.toUpperCase()}`)

const downloadReviewFile = () => {
  if (!brandingReviewBlob.value) return
  const url = URL.createObjectURL(brandingReviewBlob.value)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `review-${uploadedFileName.value || 'template'}.${reviewFileExtension.value}`)
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

const acceptBrandingPreview = () => {
  // Accept: keep file + pendingBrandingEngine for persistence on save
  closeBrandingPreviewModal()
}

const cancelBrandingPreview = () => {
  // Cancel: discard the in-memory selection. Preview never writes to Storage.
  closeBrandingPreviewModal()
  removeFile()
}

const closeBrandingPreviewModal = () => {
  showBrandingPreviewModal.value = false
  if (brandingModalMode.value === 'unsupported_format') {
    return
  }
  // Keep pendingBrandingEngine and brandingReviewBlob for accept flow
  // Only cleanup preview URL
  if (previewPdfUrl.value?.startsWith('blob:')) {
    URL.revokeObjectURL(previewPdfUrl.value)
  }
  previewPdfUrl.value = null
}

// Computed to show clean name in table
const cleanFileName = computed(() => {
  if (!uploadedFileName.value) return ''
  return uploadedFileName.value.replace(/_\d+\./, '.') // Remove timestamp
})

// Computed for name to display in Drag & Drop with smart synchronization
const displayFileName = computed(() => {
  // Priority 1: If there's a custom display name, show it
  if (addItemForm.value.documentDisplayName?.trim()) {
    return addItemForm.value.documentDisplayName.trim()
  }
  
  // Priority 2: If no display name but there's a file, show clean file name
  if (uploadedFileName.value) {
    return cleanFileName.value
  }
  
  // Priority 3: Empty if there's nothing
  return ''
})

// Computed to check if form is empty
const isFormEmpty = computed(() => {
  return !(addItemForm.value.libraryFileName?.trim() || '') && 
         !(addItemForm.value.libraryCategoryId?.trim() || '') && 
         !(addItemForm.value.description?.trim() || '') && 
         !(addItemForm.value.lawScope?.trim() || '') && 
         !(addItemForm.value.recipient?.trim() || '') && 
         !(addItemForm.value.documentDisplayName?.trim() || '') && 
         !(addItemForm.value.zoomUrl?.trim() || '') && 
         !(addItemForm.value.zoomPassword?.trim() || '') && 
         !(addItemForm.value.embedUrl?.trim() || '') && 
         !uploadedFileName.value
})

// Function to handle category selection change
const onCategoryChange = () => {
  const selectedCategory = categories.value.find(cat => cat.id === addItemForm.value.libraryCategoryId)
  if (selectedCategory) {
    addItemForm.value.libraryCategoryName = selectedCategory.name
  } else {
    addItemForm.value.libraryCategoryName = ''
  }
}

// Function to clear category selection
const clearCategory = () => {
  addItemForm.value.libraryCategoryId = ''
  addItemForm.value.libraryCategoryName = ''
}

const clearLawScope = () => {
  addItemForm.value.lawScope = ''
}

const clearRecipient = () => {
  addItemForm.value.recipient = ''
}

// Function to get file extension from filename
const getFileExtensionFromName = (filename) => {
  if (!filename) return ''
  return filename.split('.').pop().toLowerCase()
}

// Edit and Delete Functions
const editItem = (item) => {
  isEditMode.value = true
  editingItemId.value = item.id
  
  // Save original file state for comparison
  originalFileData.value = {
    hasFile: !!item.documentFileName,
    fileName: item.documentFileName || '',
    displayName: item.documentDisplayName || ''
  }

  // Save original field values to detect which fields changed on save
  originalItemData.value = {
    description: item.description || ''
  }
  
  addItemForm.value = {
    libraryFileName: item.libraryFileName || '',
    libraryCategoryId: item.libraryCategoryId || '',
    libraryCategoryName: item.libraryCategoryName || '',
    description: item.description || '',
    lawScope: item.lawScope || '',
    recipient: item.recipient || '',
    documentFileName: item.documentFileName || '',
    documentDisplayName: item.documentDisplayName || '',
    zoomUrl: item.zoomUrl || '',
    zoomPassword: item.zoomPassword || '',
    embedUrl: item.embedUrl || '',
    documentFile: null
  }
  
  uploadedFileName.value = item.documentFileName || ''
  
  showAddItemModal.value = true
}

const deleteItem = (item) => {
  if (isItemCachePending(item)) {
    return
  }
  itemToDelete.value = item
  showDeleteConfirm.value = true
}

// Admin-only manual regenerate. Two-step UX: clicking the button opens a
// confirmation modal because regeneration can take minutes for items with
// many agencies. The actual work is fire-and-forget on the backend; the
// realtime subscription mirrors the lifecycle transitions.
const regenerateItemCache = (item) => {
  if (!hasTemplateIdentifier(item) || isItemCachePending(item)) {
    return
  }
  itemToRegenerate.value = item
  showRegenerateConfirm.value = true
}

const cancelRegenerate = () => {
  itemToRegenerate.value = null
  showRegenerateConfirm.value = false
}

// Per-agency inspector. Read-only fetch + targeted regen action; never mutates
// item-level cacheStatus, never touches other agencies. The modal stays open
// while regeneration runs so the admin can re-check existence without closing.
const openCacheInspector = async (item) => {
  if (!hasTemplateIdentifier(item)) {
    return
  }
  inspectorTargetItem.value = item
  inspectorData.value = null
  inspectorLoading.value = true
  showInspectorModal.value = true
  try {
    const response = await contentLibraryTemplateService.inspectContentLibraryItemCache(item.id)
    inspectorData.value = response?.data || null
  } catch (error) {
    handleErrors('ContentLibrary.openCacheInspector', error)
    showNotification('Failed to load pre-render inspector', 'error')
    showInspectorModal.value = false
  } finally {
    inspectorLoading.value = false
  }
}

const closeCacheInspector = () => {
  showInspectorModal.value = false
  inspectorTargetItem.value = null
  inspectorData.value = null
  regeneratingAgencyId.value = null
}

const refreshCacheInspector = async () => {
  if (!inspectorTargetItem.value) return
  inspectorLoading.value = true
  try {
    const response = await contentLibraryTemplateService.inspectContentLibraryItemCache(
      inspectorTargetItem.value.id
    )
    inspectorData.value = response?.data || null
  } catch (error) {
    handleErrors('ContentLibrary.refreshCacheInspector', error)
  } finally {
    inspectorLoading.value = false
  }
}

const regenerateAgencyCache = async (targetAgencyId) => {
  if (!inspectorTargetItem.value || regeneratingAgencyId.value) return
  const target = inspectorData.value?.agencies?.find((agency) => agency.agencyId === targetAgencyId)
  if (target?.hasContentLibraryConsumers === false) return
  regeneratingAgencyId.value = targetAgencyId
  showNotification('Pre-render regeneration queued. Refresh inspector in about a minute.', 'info')
  try {
    const response = await contentLibraryTemplateService.regenerateContentLibraryAgencyCache({
      itemId: inspectorTargetItem.value.id,
      agencyId: targetAgencyId
    })
    if (response?.success && response?.status === 'queued') {
      showNotification('Pre-render regeneration queued')
    } else {
      showNotification(response?.error || 'Pre-render regeneration failed', 'error')
    }
    refreshCacheInspector()
  } catch (error) {
    handleErrors('ContentLibrary.regenerateAgencyCache', error)
    showNotification('Pre-render regeneration failed. Please refresh inspector to re-check files.', 'error')
  } finally {
    regeneratingAgencyId.value = null
  }
}

const confirmRegenerate = async () => {
  const item = itemToRegenerate.value
  if (!item) {
    return
  }
  showRegenerateConfirm.value = false

  // Optimistic flip to 'processing'; the realtime subscription mirrors backend state.
  const tableEntry = tableData.value.find((entry) => entry.id === item.id)
  if (tableEntry) {
    tableEntry.cacheStatus = 'processing'
    tableEntry.cacheError = null
  }

  try {
    await contentLibraryTemplateService.regenerateContentLibraryItemCache(item.id)
    showNotification('Pre-render regeneration queued')
  } catch (error) {
    handleErrors('ContentLibrary.regenerateItemCache', error)
    showNotification('Failed to queue pre-render regeneration', 'error')
    await loadContentItems()
  } finally {
    itemToRegenerate.value = null
  }
}

const confirmDelete = async () => {
  pushLoading()
  try {
    if (itemToDelete.value) {
      const deleteResult = await contentLibraryService.deleteItem(itemToDelete.value.id)
      await loadContentItems()
      itemToDelete.value = null
      showDeleteConfirm.value = false
      const deleteMessage = deleteResult?.documentNotFoundInStorage
        ? 'Item deleted successfully. File was not found in Storage.'
        : 'Item deleted successfully'
      showNotification(deleteMessage)
    }
  } catch (error) {
    handleErrors('ContentLibrary.deleteItem', error)
    showNotification('Error deleting item', 'error')
  } finally {
    popLoading()
  }
}

const cancelDelete = () => {
  itemToDelete.value = null
  showDeleteConfirm.value = false
}

// Add Category Modal Functions
const openAddCategoryModal = () => {
  isCategoryEditMode.value = false
  editingCategoryId.value = null
  resetAddCategoryForm()
  showAddCategoryModal.value = true
}

const closeAddCategoryModal = () => {
  showAddCategoryModal.value = false
  resetAddCategoryForm()
  isCategoryEditMode.value = false
  editingCategoryId.value = null
}

const resetAddCategoryForm = () => {
  addCategoryForm.value = {
    name: '',
    description: '',
    taxonomyGroupId: ''
  }
}

const saveAddCategory = async () => {
  isSavingCategory.value = true
  try {
    const categoryData = {
      name: addCategoryForm.value.name,
      description: addCategoryForm.value.description || null,
      taxonomyGroupId: addCategoryForm.value.taxonomyGroupId || null
    }
    
    let targetCategoryId = null
    
    if (isCategoryEditMode.value && editingCategoryId.value) {
      await contentLibraryService.updateCategory(editingCategoryId.value, categoryData)
      // Reload items also to reflect changes in categories
      await loadContentItems()
      targetCategoryId = editingCategoryId.value
      showNotification('Category updated successfully')
    } else {
      const result = await contentLibraryService.createCategory(categoryData)
      targetCategoryId = result.id
      showNotification('Category created successfully')
    }
    
    await loadCategories()
    closeAddCategoryModal()
    
    // Switch to categories tab and scroll to/highlight the category
    if (targetCategoryId) {
      activeTab.value = 1 // Switch to categories tab
      await nextTick()
      highlightAndScrollToCategory(targetCategoryId)
    }
  } catch (error) {
    handleErrors('ContentLibrary.saveAddCategory', error)
    showNotification('Error saving category', 'error')
  } finally {
    isSavingCategory.value = false
  }
}

// Category Edit and Delete Functions
const editCategory = (category) => {
  isCategoryEditMode.value = true
  editingCategoryId.value = category.id
  
  // Load category data into form
  addCategoryForm.value = {
    name: category.name,
    description: category.description,
    taxonomyGroupId: category.taxonomyGroupId || ''
  }
  showAddCategoryModal.value = true
}

const deleteCategory = (category) => {
  categoryToDelete.value = category
  showCategoryDeleteConfirm.value = true
}

const confirmDeleteCategory = async () => {
  pushLoading()
  try {
    if (categoryToDelete.value) {
      const result = await contentLibraryService.deleteCategory(categoryToDelete.value.id)
      
      if (result.success) {
        // Success case
        await loadCategories()
        closeCategoryDeleteModal()
        showNotification('Category deleted successfully')
      } else {
        // Show warning modal for associated items error
        if (result.errorType === 'associated_items') {
          categoryWarningMessage.value = 'Cannot delete category because it has associated items. Please remove or reassign the items first.'
        } else {
          showNotification(result.error || 'Error deleting category', 'error')
        }
      }
    }
  } catch (error) {
    // Handle unexpected errors (network, Firebase, etc.)
    showNotification('An unexpected error occurred. Please try again.', 'error')
  } finally {
    popLoading()
  }
}

const closeCategoryWarningModal = () => {
  categoryWarningMessage.value = ''
}

const cancelDeleteCategory = () => {
  categoryToDelete.value = null
  showCategoryDeleteConfirm.value = false
}

const closeCategoryDeleteModal = () => {
  categoryToDelete.value = null
  showCategoryDeleteConfirm.value = false
}

const clearTaxonomyGroupSelection = () => {
  addCategoryForm.value.taxonomyGroupId = ''
}

const getTaxonomyGroupName = (taxonomyGroupId) => {
  if (!taxonomyGroupId) {
    return 'The category doesnt belong to any taxonomy group'
  }

  const taxonomyGroup = taxonomyGroups.value.find((group) => group.id === taxonomyGroupId)
  return taxonomyGroup?.name || 'The category doesnt belong to any taxonomy group'
}

const selectTab = (index) => {
  if (index >= tabNames.value.length) {
    activeTab.value = 0
    return
  }
  activeTab.value = index
}

const openDescriptionModal = (description, itemNumber = null, libraryFileName = '') => {
  selectedDescription.value = description || '-'
  selectedDescriptionItem.value = {
    number: itemNumber,
    libraryFileName: libraryFileName || '-'
  }
  showDescriptionModal.value = true
}

const closeDescriptionModal = () => {
  showDescriptionModal.value = false
  selectedDescription.value = ''
  selectedDescriptionItem.value = { number: null, libraryFileName: '' }
}

</script>

<template>
  <div class="content-library-page">
    <!-- Loading Overlay -->
    <div id="loading-overlay" class="bg-white loading-overlay" v-if="loading" style="--bs-bg-opacity: 0.5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Success/Error Notification -->
    <transition name="slide-notification">
      <div 
        v-if="notification.show" 
        class="success-notification"
        :class="{ 'notification-error': notification.type === 'error' }"
      >
        <i 
          class="bi me-2" 
          :class="notification.type === 'error' ? 'bi-x-circle-fill' : 'bi-check-circle-fill'"
        ></i>
        <span class="text-14">{{ notification.message }}</span>
      </div>
    </transition>

    <!-- Back to Top Button -->
    <transition name="fade-button">
      <button
        v-if="showBackToTop"
        @click="scrollToTop"
        class="back-to-top-btn"
        title="Back to top"
      >
        <i class="bi bi-arrow-up"></i>
      </button>
    </transition>

    <!-- Header with logo and profile (without tabs) -->
    <NavBar
      :logoSrc="brandLogo"
      logoAlt="bcs logo"
      :userData="userData"
      navMode="router"
      navStyle="app"
      :navItems="[]"
      :actionItems="profileItems"
      @action-click="handleProfileClick"
      @logo-click="handleLogoClick"
      @logout="handleLogout"
    />

    <!-- Main content -->
    <div class="container-fluid mt-4 bcs-page-padding">
      <!-- Title and buttons -->
      <div class="row mb-4">
        <div class="col-12">
          <button 
            type="button" 
            class="btn btn-link p-0 mb-3 text-14 text-primary text-decoration-none" 
            @click="goBack">
            <i class="bi bi-chevron-left me-1"></i>
            <strong>Back to Home</strong>
          </button>
          <div class="d-flex align-items-center mt-4 justify-content-between">
            <h1 class="h3 mb-0 me-3">BCS Content Library</h1>
            <button
              v-if="showPrimaryAddButton"
              class="btn btn-primary"
              @click="activeTab === 0 ? openAddItemModal() : openAddCategoryModal()"
            >
              <i class="bi bi-plus-lg me-2"></i>
              {{ activeTab === 0 ? 'Add New Item' : 'Add Category' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="row">
        <div class="col-12">
          <ul class="nav nav-tabs" id="contentTabs" role="tablist">
            <li v-for="(tabName, index) in tabNames" :key="index" class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === index }"
                :id="`content-${index}-tab`"
                @click="selectTab(index)"
                type="button"
                role="tab"
                :aria-controls="`content-${index}-tab-pane`"
                :aria-selected="activeTab === index"
              >
                {{ tabName }}
              </button>
            </li>
          </ul>

          <!-- Tab Content -->
          <div class="tab-content mt-3">
            <!-- Tab 1: Content Items Table -->
            <div v-show="activeTab === 0" :class="['tab-pane', { 'show active': activeTab === 0 }]">
              <div class="content-library-table-shell">
                <div class="content-library-table-toolbar mb-3">
                  <div class="input-group">
                    <span class="input-group-text bg-white">
                      <i class="bi bi-search"></i>
                    </span>
                    <input
                      v-model="tableSearchQuery"
                      type="text"
                      class="form-control"
                      placeholder="Search by Library File Name or Document File Name"
                      aria-label="Search content library items"
                    />
                    <button
                      v-if="tableSearchQuery"
                      class="btn btn-outline-secondary"
                      type="button"
                      @click="tableSearchQuery = ''"
                      aria-label="Clear search"
                    >
                      Clear
                    </button>
                  </div>
                  <div v-if="tableSearchQuery" class="text-muted small mt-1">
                    Showing {{ filteredTableData.length }} result{{ filteredTableData.length === 1 ? '' : 's' }}
                  </div>
                </div>
                <div class="content-library-table-scroll table-responsive">
                <table class="table table-sm align-middle mb-2 content-library-admin-table" id="contentLibraryTable">
                  <thead>
                    <tr>
                      <th class="text-16 text-center content-library-col-index">
                        <strong class="text-secondary text-16">#</strong>
                      </th>
                      <th class="text-16 text-center content-library-col-file-name">
                        <strong class="text-secondary text-16">Library File Name</strong>
                      </th>
                      <th class="text-16 text-center content-library-col-category">
                        <strong class="text-secondary text-16">Library Category</strong>
                      </th>
                      <th class="text-16 text-center content-library-col-short">
                        <strong class="text-secondary text-16">Law Scope</strong>
                      </th>
                      <th class="text-16 text-center content-library-col-short">
                        <strong class="text-secondary text-16">Recipient</strong>
                      </th>
                      <th class="text-16 text-center content-library-col-cache-status">
                        <strong class="text-secondary text-16">Pre Render Status</strong>
                      </th>
                      <th class="text-16 text-center content-library-col-description">
                        <strong class="text-secondary text-16">Description</strong>
                      </th>
                      <th class="text-16 text-center content-library-col-zoom-url">
                        <strong class="text-secondary text-16">Zoom URL</strong>
                      </th>
                      <th class="text-16 text-center content-library-col-icon">
                        <strong class="text-secondary text-16">Image</strong>
                      </th>
                      <th class="text-16 text-center actions-cell content-library-sticky-actions">
                        <strong class="text-secondary text-16">Actions</strong>
                      </th>
                    </tr>
                  </thead>
                  <tbody id="contentLibraryTableBody">
                    <tr 
                      v-for="(item, index) in filteredTableData" 
                      :key="item.id" 
                      :id="`contentLibraryRow-${item.id}`"
                    >
                      <td class="text-16 text-center">{{ index + 1 }}</td>
                      <td class="text-16 text-center content-library-col-file-name">
                        <span
                          class="content-library-cell-truncate"
                          :title="item.libraryFileName || '-'"
                        >
                          {{ item.libraryFileName || '-' }}
                        </span>
                      </td>
                      <td class="text-16 text-center content-library-col-category">
                        <span
                          class="content-library-cell-truncate"
                          :title="item.libraryCategoryName || '-'"
                        >
                          {{ item.libraryCategoryName || '-' }}
                        </span>
                      </td>
                      <td class="text-16 text-center">{{ item.lawScope || '-' }}</td>
                      <td class="text-16 text-center">{{ item.recipient || '-' }}</td>
                      <td class="text-16 text-center">
                        <div v-if="hasTemplateIdentifier(item)" class="d-flex justify-content-center">
                          <span
                            v-if="item.cacheStatus === 'failed'"
                            class="badge bg-danger d-inline-flex align-items-center"
                            :title="item.cacheError || 'Cache generation failed'"
                          >
                            <i class="bi bi-exclamation-triangle-fill me-1"></i>
                            Failed
                          </span>
                          <span
                            v-else-if="item.cacheStatus === 'processed'"
                            class="badge bg-success d-inline-flex align-items-center"
                            title="Pre-rendered files are ready"
                          >
                            <i class="bi bi-check-circle-fill me-1"></i>
                            Ready
                          </span>
                          <span
                            v-else-if="isItemCachePending(item)"
                            class="badge bg-warning text-dark d-inline-flex align-items-center"
                            title="Pre-rendering files…"
                          >
                            <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Pre-rendering
                          </span>
                          <span v-else class="text-muted">-</span>
                        </div>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td class="text-16 text-center">
                        <span
                          class="description-cell content-library-cell-truncate content-library-cell-truncate--description"
                          :title="item.description || '-'"
                          @click="openDescriptionModal(item.description, index + 1, item.libraryFileName)"
                        >
                          {{ truncateText(item.description, 90) }}
                        </span>
                        <button
                          v-if="item.description"
                          type="button"
                          class="btn btn-link p-0 ms-1 read-more-link"
                          @click="openDescriptionModal(item.description, index + 1, item.libraryFileName)"
                        >
                          Read more
                        </button>
                      </td>
                      <td class="text-16 text-center">
                        <a 
                          v-if="item.zoomUrl" 
                          :href="item.zoomUrl" 
                          target="_blank" 
                          class="text-primary text-decoration-none"
                          :title="item.zoomUrl"
                        >
                          <i class="bi bi-link-45deg me-1"></i>
                          <span class="content-library-cell-truncate content-library-cell-truncate--zoom">{{ truncateText(item.zoomUrl, 28) }}</span>
                        </a>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td class="text-16 text-center">
                        <div v-if="item.fileType" class="file-type-icon d-flex align-items-center justify-content-center" style="width: 50px; height: 50px; margin: 0 auto;">
                          <i 
                            v-if="item.fileType === 'PDF'" 
                            class="bi bi-filetype-pdf text-danger fs-4"
                            title="PDF Document"
                          ></i>
                          <i 
                            v-else-if="item.fileType === 'XLS'" 
                            class="bi bi-file-earmark-excel text-success fs-4"
                            title="Excel Spreadsheet"
                          ></i>
                          <i 
                            v-else-if="item.fileType === 'DOC'" 
                            class="bi bi-file-earmark-word text-primary fs-4"
                            title="Word Document"
                          ></i>
                          <i 
                            v-else-if="item.fileType === 'PPT'" 
                            class="bi bi-file-earmark-ppt-fill text-danger fs-4"
                            title="PowerPoint Presentation"
                          ></i>
                        </div>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td class="text-16 text-center actions-cell content-library-sticky-actions">
                        <div class="d-flex gap-2 justify-content-center">
                          <button class="btn btn-sm btn-outline-primary" @click="editItem(item)" style="cursor: pointer;">
                            <i class="bi bi-pencil"></i>
                          </button>
                          <button
                            v-if="hasTemplateIdentifier(item)"
                            class="btn btn-sm btn-outline-primary"
                            @click="openCacheInspector(item)"
                            title="Inspect per-agency pre-render"
                            style="cursor: pointer;"
                          >
                            <i class="bi bi-binoculars-fill"></i>
                          </button>
                          <button
                            v-if="hasTemplateIdentifier(item)"
                            class="btn btn-sm btn-outline-secondary"
                            @click="regenerateItemCache(item)"
                            :disabled="isItemCachePending(item)"
                            :title="isItemCachePending(item) ? 'Regeneration already in progress' : 'Regenerate pre-render for all agencies'"
                            style="cursor: pointer;"
                          >
                            <i class="bi bi-arrow-clockwise"></i>
                          </button>
                          <button
                            class="btn btn-sm btn-outline-danger"
                            @click="deleteItem(item)"
                            :disabled="isItemCachePending(item)"
                            :title="isItemCachePending(item) ? 'Cannot delete while pre-render is in progress' : ''"
                            style="cursor: pointer;"
                          >
                            <i class="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="!filteredTableData.length">
                      <td colspan="10" class="text-center py-4 text-gray-600">No results</td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </div>
            </div>

            <!-- Tab 2: Categories Configuration  -->
            <div v-show="activeTab === 1" :class="['tab-pane fade', { 'show active': activeTab === 1 }]">
              <div class="row">
                <div class="col-12">
                  <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                      <h5 class="mb-0">Categories Configuration</h5>
                    </div>
                    <div class="card-body">
                      <div class="list-group">
                        <div v-for="category in categories" :key="category.id" class="list-group-item" :id="`categoryItem-${category.id}`">
                          <div class="d-flex justify-content-between align-items-center">
                            <div class="category-list-copy">
                              <h6 class="category-list-title">{{ category.name }}</h6>
                              <p class="category-list-description text-muted">{{ category.description }}</p>
                              <div class="category-taxonomy-group-wrap">
                                <span
                                  :class="[
                                    'category-taxonomy-group-pill',
                                    category.taxonomyGroupId ? 'category-taxonomy-group-pill--assigned' : 'category-taxonomy-group-pill--unassigned'
                                  ]"
                                >
                                  {{ category.taxonomyGroupId ? getTaxonomyGroupName(category.taxonomyGroupId) : 'Unassigned' }}
                                </span>
                              </div>
                            </div>
                            <div class="d-flex gap-2">
                              <button class="btn btn-sm btn-outline-primary" @click="editCategory(category)" style="cursor: pointer;">
                                <i class="bi bi-pencil"></i>
                              </button>
                              <button class="btn btn-sm btn-outline-danger" @click="deleteCategory(category)" style="cursor: pointer;">
                                <i class="bi bi-trash"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tab 3: Agency pre-render audit (admin only) -->
            <div
              v-if="agencyPrerenderTabIndex >= 0"
              v-show="activeTab === agencyPrerenderTabIndex"
              :class="['tab-pane fade', { 'show active': activeTab === agencyPrerenderTabIndex }]"
            >
              <ContentLibraryAgencyPrerenderTab
                :active="activeTab === agencyPrerenderTabIndex"
                @notify="handleAgencyPrerenderNotify"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Description Modal -->
    <div
      v-if="showDescriptionModal"
      class="modal fade show"
      tabindex="-1"
      style="display: block; background-color: rgba(0, 0, 0, 0.5);"
      @click="closeDescriptionModal"
    >
      <div class="modal-dialog modal-dialog-centered modal-description-compact" @click.stop>
        <div class="modal-content">
          <div class="modal-header px-4 d-flex flex-column bg-gray-200">
            <div class="d-flex w-100 justify-content-between">
              <span class="modal-title header-22">
                <div>
                  <i class="bi bi-card-text me-2 text-primary"></i>
                  <strong>Full Description</strong>
                </div>
              </span>
              <button
                type="button"
                class="btn-close"
                @click="closeDescriptionModal"
                style="cursor: pointer;"
                aria-label="Close"
              ></button>
            </div>
            <p class="me-auto mb-0 text-16 text-gray-700 mt-1">
              Review the complete content library description.
            </p>
          </div>

          <div class="modal-body p-4">
            <div class="description-modal-meta text-start mb-3">
              <div class="text-14 fw-semibold text-dark">
                #{{ selectedDescriptionItem.number || '-' }} - {{ selectedDescriptionItem.libraryFileName || '-' }}
              </div>
            </div>
            <div class="description-modal-body text-start text-14">
              {{ selectedDescription }}
            </div>
          </div>

          <div class="modal-footer border-0 p-4 pt-0">
            <button type="button" class="btn btn-primary" @click="closeDescriptionModal" style="cursor: pointer;">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Item Modal -->
    <div
      v-if="showAddItemModal"
      class="modal fade show"
      tabindex="-1"
      style="display: block; background-color: rgba(0, 0, 0, 0.5);"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <!-- Modal Header -->
          <div class="modal-header px-4 d-flex flex-column bg-gray-200">
            <div class="d-flex w-100 justify-content-between">
              <span class="modal-title header-22">
                <div>
                  <i class="bi bi-plus-lg me-2" v-if="!isEditMode"></i>
                  <i class="bi bi-pencil me-2" v-else></i>
                  <strong>{{ isEditMode ? 'Edit Item' : 'Add New Item' }}</strong>
                </div>
              </span>
              <button
                type="button"
                class="btn-close"
                @click="closeAddItemModal"
                style="cursor: pointer; margin-top: 4px; margin-right: -4px;"
                aria-label="Close">
              </button>
            </div>
            <p class="me-auto mb-0 text-16 text-gray-700 mt-1">
              Add a new item to the content library
            </p>
          </div>

          <!-- Modal Body -->
          <div class="modal-body p-4">
            <!-- Warning for no categories -->
            <div v-if="categories.length === 0" class="warning-box mb-4">
              <i class="bi bi-exclamation-triangle warning-icon"></i>
              <span class="warning-text text-12">
                <strong>
                  No categories available. Please create at least one category before adding items.<br />
                  Go to the Categories tab to create categories.
                </strong>
              </span>
            </div>

            <!-- Form -->
            <form @submit.prevent="saveAddItem">
              <div class="row g-3">
                <!-- Library File Name -->
                <div class="col-md-6">
                  <label for="libraryFileName" class="form-label text-14 fw-semibold">Library File Name</label>
                  <input 
                    type="text" 
                    class="form-control form-control-sm" 
                    id="libraryFileName"
                    v-model="addItemForm.libraryFileName"
                    placeholder="e.g., Employee Handbook 2024"
                  />
                </div>

                <!-- Library Category -->
                <div class="col-md-6">
                  <label for="libraryCategory" class="form-label text-14 fw-semibold">Library Category</label>
                  <div class="position-relative">
                    <select 
                      class="form-select form-select-sm category-select" 
                      id="libraryCategory"
                      v-model="addItemForm.libraryCategoryId"
                      @change="onCategoryChange"
                      :disabled="categories.length === 0"
                    >
                      <option value="" disabled>Select a category</option>
                      <option 
                        v-for="category in categories" 
                        :key="category.id" 
                        :value="category.id"
                      >
                        {{ category.name }}
                      </option>
                    </select>
                    <button 
                      v-if="addItemForm.libraryCategoryId"
                      type="button"
                      class="btn position-absolute clear-category-btn"
                      @click="clearCategory"
                      title="Clear selection"
                    >
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </div>
                </div>

                <div class="col-md-12">
                  <label for="itemDescription" class="form-label text-14 fw-semibold">Description</label>
                  <textarea
                    id="itemDescription"
                    v-model="addItemForm.description"
                    class="form-control form-control-sm"
                    placeholder="Enter a short description for this item"
                    rows="3"
                  ></textarea>
                </div>

                <div class="col-md-6">
                  <label for="lawScope" class="form-label text-14 fw-semibold">Law Scope</label>
                  <div class="position-relative">
                    <select
                      id="lawScope"
                      v-model="addItemForm.lawScope"
                      class="form-select form-select-sm category-select"
                    >
                      <option value="">Select law scope</option>
                      <option
                        v-for="option in lawScopeOptions"
                        :key="option"
                        :value="option"
                      >
                        {{ option }}
                      </option>
                    </select>
                    <button
                      v-if="addItemForm.lawScope"
                      type="button"
                      class="btn position-absolute clear-category-btn"
                      @click="clearLawScope"
                      title="Clear selection"
                      aria-label="Clear law scope selection"
                      aria-controls="lawScope"
                    >
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </div>
                </div>

                <div class="col-md-6">
                  <label for="recipient" class="form-label text-14 fw-semibold">Recipient</label>
                  <div class="position-relative">
                    <select
                      id="recipient"
                      v-model="addItemForm.recipient"
                      class="form-select form-select-sm category-select"
                    >
                      <option value="">Select recipient</option>
                      <option
                        v-for="option in recipientOptions"
                        :key="option"
                        :value="option"
                      >
                        {{ option }}
                      </option>
                    </select>
                    <button
                      v-if="addItemForm.recipient"
                      type="button"
                      class="btn position-absolute clear-category-btn"
                      @click="clearRecipient"
                      title="Clear selection"
                      aria-label="Clear recipient selection"
                      aria-controls="recipient"
                    >
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </div>
                </div>

                <!-- Zoom URL -->
                <div class="col-md-6">
                  <label for="zoomUrl" class="form-label text-14 fw-semibold">Zoom URL</label>
                  <input 
                    type="url" 
                    class="form-control form-control-sm" 
                    id="zoomUrl"
                    v-model="addItemForm.zoomUrl"
                    placeholder="https://zoom.us/j/123456789"
                  />
                </div>
                  
              <!-- Zoom Password -->
                <div class="col-md-6">
                  <label for="zoomPassword" class="form-label text-14 fw-semibold">Zoom Password</label>
                  <input 
                    type="text" 
                    class="form-control form-control-sm" 
                    id="zoomPassword"
                    v-model="addItemForm.zoomPassword"
                    placeholder="e.g., Abc123!"
                  />
                </div>

                <!-- Embed URL -->
                <div class="col-md-12">
                  <label for="embedUrl" class="form-label text-14 fw-semibold">Embed URL</label>
                  <input
                    type="url"
                    class="form-control form-control-sm"
                    id="embedUrl"
                    v-model="addItemForm.embedUrl"
                    placeholder="https://bcsvibetools.web.app/compliance-notices-pro"
                  />
                  <small class="text-muted">Optional. Opens this item as an embedded tool inside BCS Pro.</small>
                </div>

                <!-- Document File Name -->
                <div class="col-md-6">
                  <label for="documentDisplayName" class="form-label text-14 fw-semibold">Document File Name</label>
                  <input 
                    type="text" 
                    class="form-control form-control-sm" 
                    id="documentDisplayName"
                    v-model="addItemForm.documentDisplayName"
                    placeholder="e.g., 2025 Employee Requirements"
                  />
                  <small class="text-muted">Leave empty to use original file name</small>
                </div>

                <!-- Document Upload (Drag & Drop) -->
                <div class="col-md-12">
                  <label class="form-label text-14 fw-semibold">Document File</label>
                  <div 
                    class="upload-document text-center p-4 border border-2 border-dashed border-light rounded position-relative"
                    @click="openFilePicker" 
                    @dragover.prevent="isDragOver = true"
                    @dragleave.prevent="isDragOver = false"
                    @drop.prevent="onDrop"
                    :class="{ 'dragover': isDragOver }"
                  >
                    <input 
                      type="file" 
                      ref="fileInput"
                      accept=".pdf,.doc,.docx,.dotx,.dotm,.docm,.rtf,.xls,.xlsx,.xltx,.xltm,.xlsm,.ods,.ppt,.pptx,.ppsx,.pptm,.odp"
                      style="display: none"
                      @change="onInputChange"
                    />
                    
                    <!-- Remove button - only show when file is uploaded -->
                    <button 
                      v-if="uploadedFileName"
                      type="button" 
                      class="btn btn-sm btn-outline-danger position-absolute top-0 end-0 m-2"
                      @click.stop="removeFile"
                      style="border-radius: 50%; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center;"
                    >
                      <i class="bi bi-x"></i>
                    </button>
                    
                    <!-- Upload state (no file) -->
                    <div v-if="!uploadedFileName">
                      <i class="bi bi-file-earmark-arrow-up text-primary" style="font-size: 2.5rem;"></i>
                      
                      <div class="upload-document-action text-primary fw-bold mt-2">
                        <div class="fw-medium text-dark mb-1">
                          Upload Document
                        </div>
                        <div class="text-muted">or drag and drop your files here</div>
                        <small class="text-muted">Accept: PDF, DOC, DOCX, DOTX, DOTM, DOCM, RTF, XLS, XLSX, XLTX, XLTM, XLSM, PPT, PPTX, PPSX, PPTM, ODP (Max 10MB)</small>
                      </div>
                    </div>
                    
                    <!-- File uploaded state -->
                    <div v-else class="py-2">
                      <!-- Dynamic file type icon -->
                      <i 
                        v-if="getFileExtensionFromName(uploadedFileName) === 'pdf'" 
                        class="bi bi-file-earmark-pdf text-danger" 
                        style="font-size: 3rem;"
                      ></i>
                      <i 
                        v-else-if="isExcelFile(uploadedFileName)" 
                        class="bi bi-file-earmark-excel text-success" 
                        style="font-size: 3rem;"
                      ></i>
                      <i 
                        v-else-if="isWordFile(uploadedFileName)" 
                        class="bi bi-file-earmark-word text-primary" 
                        style="font-size: 3rem;"
                      ></i>
                      <i 
                        v-else-if="isPowerPointFile(uploadedFileName)" 
                        class="bi bi-file-earmark-ppt-fill text-danger" 
                        style="font-size: 3rem;"
                      ></i>
                      <i 
                        v-else 
                        class="bi bi-file-earmark-text text-secondary" 
                        style="font-size: 3rem;"
                      ></i>
                      
                      <!-- File name -->
                      <div
                        class="upload-document-file-name fw-bold mt-3 text-dark"
                        :title="displayFileName"
                      >
                        {{ displayFileName }}
                      </div>
                      
                      <div class="text-muted small mt-1">
                        <template v-if="brandingDetection.state === 'unsupported_format'">
                          File selected — save blocked until you remove it or use a supported template
                        </template>
                        <template v-else>
                          File uploaded successfully
                        </template>
                      </div>

                      <!-- Branding tag detection indicators -->
                      <div v-if="brandingDetection.state === 'analyzing'" class="branding-detection-indicator mt-2 d-flex align-items-center justify-content-center gap-2">
                        <span class="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>
                        <span class="text-primary small fw-medium">Analyzing template...</span>
                      </div>
                      <div v-else-if="brandingDetection.state === 'detected'" class="branding-detection-indicator mt-2 d-flex align-items-center justify-content-center gap-2">
                        <i class="bi bi-check-circle-fill text-success"></i>
                        <span class="text-success small fw-medium">Branding template detected</span>
                      </div>
                      <div v-else-if="brandingDetection.state === 'uncertain'" class="branding-detection-indicator mt-2 d-flex align-items-center justify-content-center gap-2">
                        <i class="bi bi-question-circle-fill" style="color: #6f42c1;"></i>
                        <span class="small fw-medium" style="color: #6f42c1;">Could not fully verify this file</span>
                      </div>
                      <div v-else-if="brandingDetection.state === 'not_detected'" class="branding-detection-indicator mt-2 d-flex align-items-center justify-content-center gap-2">
                        <i class="bi bi-dash-circle text-secondary"></i>
                        <span class="text-secondary small">No branding tags found</span>
                      </div>
                      <div v-else-if="brandingDetection.state === 'unsupported_format'" class="branding-detection-indicator mt-2 d-flex align-items-center justify-content-center gap-2">
                        <i class="bi bi-exclamation-triangle-fill text-warning"></i>
                        <span class="text-warning small fw-medium">Template tags found — format not supported yet</span>
                      </div>
                      <div v-else-if="brandingDetection.state === 'error'" class="branding-detection-indicator mt-2 d-flex align-items-center justify-content-center gap-2">
                        <i class="bi bi-exclamation-triangle text-warning"></i>
                        <span class="text-warning small">There was a problem analyzing this file</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Form Actions -->
              <div class="d-flex justify-content-end gap-2 mt-4">
                <button type="button" class="btn btn-outline-primary text-16" @click="closeAddItemModal" style="cursor: pointer;">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" style="cursor: pointer;" :disabled="isSavingItem || isFormEmpty">
                  {{ isSavingItem ? 'Saving...' : 'Save Item' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Pre-render inspector modal -->
    <div
      v-if="showInspectorModal"
      class="modal fade show"
      tabindex="-1"
      style="display: block; background-color: rgba(0, 0, 0, 0.5);"
    >
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl">
        <div class="modal-content">
          <div class="modal-header px-4 d-flex flex-column bg-gray-200">
            <div class="d-flex w-100 justify-content-between">
              <span class="modal-title header-22">
                <div>
                  <i class="bi bi-binoculars-fill me-2 text-primary"></i>
                  <strong>Pre-render inspector</strong>
                </div>
              </span>
              <button type="button" class="btn-close" @click="closeCacheInspector" style="cursor: pointer;" aria-label="Close"></button>
            </div>
            <p class="me-auto mb-0 text-16 text-gray-700 mt-1" v-if="inspectorTargetItem">
              {{ inspectorTargetItem.libraryFileName }} - {{ inspectorTargetItem.documentFileName }}
            </p>
          </div>
          <div class="modal-body p-4">
            <div v-if="inspectorLoading" class="text-center py-4">
              <div class="spinner-border text-info" role="status" aria-hidden="true"></div>
              <p class="text-muted mt-2 mb-0">Checking Storage...</p>
            </div>
            <div v-else-if="inspectorData">
              <h6 class="mb-2">Agencies ({{ inspectorData.agencies.length }})</h6>
              <p class="text-muted small mb-3">Every agency gets a default variant; white-label-eligible agencies also get a branded variant.</p>
              <div v-if="isRegeneratingAgencyCache" class="alert alert-info small py-2 px-3">
                Regeneration is running for one agency. This call is synchronous and may take up to a minute.
              </div>
              <div v-if="!inspectorData.agencies.length" class="text-muted">No agencies registered.</div>
              <div v-else class="table-responsive">
                <table class="table table-sm align-middle cache-inspector-table" style="table-layout: fixed; width: 100%;">
                  <colgroup>
                    <col style="width: 50%">
                    <col style="width: 25%">
                    <col style="width: 25%">
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Agency</th>
                      <th>Formats</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="agency in inspectorData.agencies" :key="agency.agencyId">
                      <td>
                        <div class="fw-semibold">{{ agency.name }}</div>
                        <div class="text-muted small text-break">{{ agency.agencyId }}</div>
                        <span
                          v-if="agency.hasContentLibraryConsumers === false"
                          class="badge bg-secondary mt-1"
                        >No Content Library access</span>
                      </td>
                      <td>
                        <div class="d-flex flex-column gap-1">
                          <div
                            v-for="entry in agency.formats"
                            :key="`${agency.agencyId}-${entry.format}`"
                            class="d-flex align-items-center column-gap-3"
                          >
                            <span class="text-uppercase small fw-semibold" style="min-width: 3rem;">{{ entry.format }}</span>
                            <span class="d-inline-flex align-items-center" :title="entry.defaultExists ? 'Default variant cached' : 'Default variant missing'">
                              <i :class="entry.defaultExists ? 'bi bi-check-circle-fill text-success' : 'bi bi-exclamation-circle-fill text-warning'" class="me-1"></i>
                              <span class="small">Default</span>
                            </span>
                            <span
                              v-if="agency.whitelabelEligible"
                              class="d-inline-flex align-items-center"
                              :title="entry.whitelabelExists ? 'White-label variant cached' : 'White-label variant missing'"
                            >
                              <i :class="entry.whitelabelExists ? 'bi bi-check-circle-fill text-success' : 'bi bi-exclamation-circle-fill text-warning'" class="me-1"></i>
                              <span class="small">White-label</span>
                            </span>
                            <span v-else class="text-muted small">No white-label (missing logo or color)</span>
                          </div>
                        </div>
                      </td>
                      <td class="text-end">
                        <button
                          class="btn btn-sm btn-outline-secondary"
                          @click="regenerateAgencyCache(agency.agencyId)"
                          :disabled="regeneratingAgencyId === agency.agencyId || agency.hasContentLibraryConsumers === false"
                          :title="agency.hasContentLibraryConsumers === false
                            ? 'This agency has no Content Library module or users with access'
                            : 'Regenerate pre-render for this agency'"
                          style="cursor: pointer;"
                        >
                          <span v-if="regeneratingAgencyId === agency.agencyId" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          <i v-else class="bi bi-arrow-clockwise me-1"></i>
                          {{ regeneratingAgencyId === agency.agencyId ? 'Regenerating...' : 'Regenerate' }}
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 p-4">
            <button type="button" class="btn btn-outline-secondary" @click="closeCacheInspector" style="cursor: pointer;">Close</button>
            <button type="button" class="btn btn-outline-primary" @click="refreshCacheInspector" :disabled="inspectorLoading" style="cursor: pointer;">
              <i class="bi bi-arrow-repeat me-1"></i>
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Regenerate Cache Confirmation Modal -->
    <div
      v-if="showRegenerateConfirm"
      class="modal fade show"
      tabindex="-1"
      style="display: block; background-color: rgba(0, 0, 0, 0.5);"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header px-4 d-flex flex-column bg-gray-200">
            <div class="d-flex w-100 justify-content-between">
              <span class="modal-title header-22">
                <div>
                  <i class="bi bi-arrow-clockwise me-2 text-warning"></i>
                  <strong>Regenerate pre-render</strong>
                </div>
              </span>
              <button
                type="button"
                class="btn-close"
                @click="cancelRegenerate"
                style="cursor: pointer;"
                aria-label="Close"></button>
            </div>
            <p class="me-auto mb-0 text-16 text-gray-700 mt-1">
              This will re-render the default file and every white-label variant for this item. It may take several minutes.
            </p>
          </div>

          <div class="modal-body p-4">
            <div v-if="itemToRegenerate" class="text-center">
              <div class="mb-3">
                <i class="bi bi-arrow-clockwise text-warning fs-1"></i>
              </div>
              <h6 class="mb-2">{{ itemToRegenerate.libraryFileName }}</h6>
              <p class="text-muted mb-0">{{ itemToRegenerate.libraryCategoryName || '-' }}</p>
              <p class="text-muted small mb-0">{{ itemToRegenerate.documentFileName }}</p>
            </div>
          </div>

          <div class="modal-footer border-0 p-4">
            <button type="button" class="btn btn-outline-secondary" @click="cancelRegenerate" style="cursor: pointer;">
              Cancel
            </button>
            <button type="button" class="btn btn-warning" @click="confirmRegenerate" style="cursor: pointer;">
              <i class="bi bi-arrow-clockwise me-1"></i>
              Regenerate
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Item Confirmation Modal -->
    <div 
      v-if="showDeleteConfirm" 
      class="modal fade show" 
      tabindex="-1" 
      style="display: block; background-color: rgba(0, 0, 0, 0.5);"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <!-- Modal Header -->
          <div class="modal-header px-4 d-flex flex-column bg-gray-200">
            <div class="d-flex w-100 justify-content-between">
              <span class="modal-title header-22">
                <div>
                  <i class="bi bi-exclamation-triangle me-2 text-danger"></i>
                  <strong>Confirm Delete</strong>
                </div>
              </span>
              <button
                type="button"
                class="btn-close"
                @click="cancelDelete"
                style="cursor: pointer;"
                aria-label="Close"></button>
            </div>
            <p class="me-auto mb-0 text-16 text-gray-700 mt-1">
              Are you sure you want to delete this item?
            </p>
          </div>

          <!-- Modal Body -->
          <div class="modal-body p-4">
            <div v-if="itemToDelete" class="text-center">
              <div class="mb-3">
                <i class="bi bi-file-earmark text-danger fs-1"></i>
              </div>
              <h6 class="mb-2">{{ itemToDelete.libraryFileName }}</h6>
              <p class="text-muted mb-0">{{ itemToDelete.libraryCategoryName || '-' }}</p>
              <p class="text-muted small mb-0">{{ itemToDelete.documentFileName }}</p>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="modal-footer border-0 p-4">
            <button type="button" class="btn btn-outline-secondary" @click="cancelDelete" style="cursor: pointer;">
              Cancel
            </button>
            <button type="button" class="btn btn-danger" @click="confirmDelete" style="cursor: pointer;">
              <i class="bi bi-trash me-1"></i>
              Delete Item
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Category Modal -->
    <div 
      v-if="showAddCategoryModal" 
      class="modal fade show" 
      tabindex="-1" 
      style="display: block; background-color: rgba(0, 0, 0, 0.5);"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <!-- Modal Header -->
          <div class="modal-header px-4 d-flex flex-column bg-gray-200">
            <div class="d-flex w-100 justify-content-between">
              <span class="modal-title header-22">
                <div>
                  <i class="bi bi-plus-lg me-2" v-if="!isCategoryEditMode"></i>
                  <i class="bi bi-pencil me-2" v-else></i>
                  <strong>{{ isCategoryEditMode ? 'Edit Category' : 'Add New Category' }}</strong>
                </div>
              </span>
              <button
                type="button"
                class="btn-close"
                @click="closeAddCategoryModal"
                style="cursor: pointer;"
                aria-label="Close"></button>
            </div>
            <p class="me-auto mb-0 text-16 text-gray-700 mt-1">
              Create a new category for organizing content
            </p>
          </div>

          <!-- Modal Body -->
          <div class="modal-body p-4">
            <form @submit.prevent="saveAddCategory">
              <div class="mb-3">
                <label for="categoryName" class="form-label text-14 fw-semibold">Category Name</label>
                <input 
                  type="text" 
                  class="form-control form-control-sm" 
                  id="categoryName"
                  v-model="addCategoryForm.name"
                  placeholder="Enter category name"
                  required
                />
              </div>
              
              <div class="mb-4">
                <label for="categoryDescription" class="form-label text-14 fw-semibold">Category Description <span class="text-muted">(Optional)</span></label>
                <textarea 
                  class="form-control form-control-sm" 
                  id="categoryDescription"
                  v-model="addCategoryForm.description"
                  placeholder="Enter category description"
                  rows="3"
                ></textarea>
              </div>

              <div class="mb-4">
                <label for="categoryTaxonomyGroup" class="form-label text-14 fw-semibold">Taxonomy Group <span class="text-muted">(Optional)</span></label>
                <div class="position-relative">
                  <select
                    id="categoryTaxonomyGroup"
                    v-model="addCategoryForm.taxonomyGroupId"
                    class="form-select form-select-sm category-select"
                  >
                    <option value="">Unassigned</option>
                    <option
                      v-for="taxonomyGroup in taxonomyGroups"
                      :key="taxonomyGroup.id"
                      :value="taxonomyGroup.id"
                    >
                      {{ taxonomyGroup.name }}
                    </option>
                  </select>
                  <button
                    v-if="addCategoryForm.taxonomyGroupId"
                    type="button"
                    class="btn position-absolute clear-category-btn"
                    @click="clearTaxonomyGroupSelection"
                    title="Clear selection"
                  >
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
              
              <!-- Form Actions -->
              <div class="d-flex justify-content-end gap-2">
                <button type="button" class="btn btn-outline-secondary" @click="closeAddCategoryModal" style="cursor: pointer;">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" style="cursor: pointer;" :disabled="isSavingCategory">
                  {{ isSavingCategory ? 'Saving...' : 'Save Category' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Category Confirmation Modal -->
    <div 
      v-if="showCategoryDeleteConfirm" 
      class="modal fade show" 
      tabindex="-1" 
      style="display: block; background-color: rgba(0, 0, 0, 0.5);"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <!-- Modal Header -->
          <div class="modal-header px-4 d-flex flex-column bg-gray-200">
            <div class="d-flex w-100 justify-content-between">
              <span class="modal-title header-22">
                <div>
                  <i class="bi bi-exclamation-triangle me-2 text-danger"></i>
                  <strong>Confirm Delete Category</strong>
                </div>
              </span>
              <button
                type="button"
                class="btn-close"
                @click="cancelDeleteCategory"
                style="cursor: pointer;"
                aria-label="Close"></button>
            </div>
            <p class="me-auto mb-0 text-16 text-gray-700 mt-1">
              Are you sure you want to delete this category?
            </p>
          </div>

          <!-- Modal Body -->
          <div class="modal-body p-4">
            <div v-if="categoryToDelete" class="text-center">
              <div class="mb-3">
                <i class="bi bi-folder text-danger fs-1"></i>
              </div>
              <h6 class="mb-2">{{ categoryToDelete.name }}</h6>
              <p class="text-muted mb-0">{{ categoryToDelete.description }}</p>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="modal-footer border-0 p-4">
            <button type="button" class="btn btn-outline-secondary" @click="cancelDeleteCategory" style="cursor: pointer;">
              Cancel
            </button>
            <button type="button" class="btn btn-danger" @click="confirmDeleteCategory" style="cursor: pointer;">
              <i class="bi bi-trash me-1"></i>
              Delete Category
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Category Warning Modal -->
    <div 
      v-if="categoryWarningMessage" 
      class="modal fade show" 
      tabindex="-1" 
      style="display: block; background-color: rgba(0, 0, 0, 0.5);"
      @click="categoryWarningMessage = ''"
    >
      <div class="modal-dialog modal-dialog-centered modal-sm modal-warning-wide" @click.stop>
        <div class="modal-content">
          <!-- Modal Body -->
          <div class="modal-body p-4 text-center">
            <!-- Close Button (X) -->
            <div class="d-flex justify-content-end mb-3">
              <button
                type="button"
                class="btn-close"
                @click="closeCategoryWarningModal"
                style="cursor: pointer;"
                aria-label="Close"></button>
            </div>
            
            <!-- Warning Icon (smaller) -->
            <div class="mb-3">
              <i class="bi bi-exclamation-triangle text-danger" style="font-size: 2rem;"></i>
            </div>
            
            <!-- Warning Message (clean, no background) -->
            <div class="premium-warning-message">
              <p class="mb-2" style="color: #2d3748; font-weight: 600; font-size: 16px;">
                Cannot delete category "{{ categoryToDelete?.name }}"
              </p>
              <p class="mb-0" style="color: #718096; font-size: 14px; line-height: 1.5;">
                Please remove or reassign the items first
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Branding Template Preview Modal -->
    <div
      v-if="showBrandingPreviewModal"
      class="modal fade show"
      tabindex="-1"
      style="display: block; background-color: rgba(0, 0, 0, 0.5); z-index: 1060;"
    >
      <div class="modal-dialog modal-dialog-centered branding-preview">
        <div class="modal-content">
          <!-- Modal Header -->
          <div class="modal-header px-4 d-flex flex-column bg-gray-200">
            <div class="d-flex w-100 justify-content-between">
              <span class="modal-title header-22">
                <div>
                  <i
                    :class="brandingModalMode === 'unsupported_format'
                      ? 'bi bi-exclamation-triangle me-2 text-warning'
                      : 'bi bi-file-earmark-check me-2 text-primary'"></i>
                  <strong>{{ brandingModalMode === 'unsupported_format'
                    ? 'Branding template detected'
                    : 'Please review the document' }}</strong>
                </div>
              </span>
              <button
                type="button"
                class="btn-close"
                @click="cancelBrandingPreview"
                style="cursor: pointer;"
                aria-label="Close"></button>
            </div>
            <p class="me-auto mb-0 text-16 text-gray-700 mt-1">
              <template v-if="brandingModalMode === 'unsupported_format'">
                This file looks like a branding template, but automated branding is not available for this format yet.
              </template>
              <template v-else>
                Please review the file and make sure the rendering looks good
              </template>
            </p>
          </div>

          <!-- Modal Body: unsupported format (tags found, engine not available) -->
          <div v-if="brandingModalMode === 'unsupported_format'" class="modal-body p-4">
            <div class="d-flex flex-column align-items-center text-center gap-2 mb-3">
              <span class="badge bg-warning text-dark">
                <i class="bi bi-tags me-1"></i>Template tags detected
              </span>
              <span class="text-muted small branding-preview-file-name">{{ uploadedFileName }}</span>
            </div>
            <div class="alert alert-warning small mb-0">
              <p class="mb-2">
                Branding markers were found in this document. It should not be published as a direct download
                because end users would receive an unbranded template.
              </p>
              <p class="mb-2">
                Automated branding is only supported for <strong>{{ SUPPORTED_BRANDING_FORMATS_LABEL }}</strong> files today.
                OpenDocument and legacy Office formats are not supported yet.
              </p>
              <p class="mb-0 text-muted">
                Remove this file and upload a supported template, or contact your administrator if you need this format enabled.
              </p>
            </div>
          </div>

          <!-- Modal Body: branded preview flow -->
          <div v-else class="modal-body p-4">
            <!-- Detection state badge -->
            <div class="d-flex flex-column align-items-center text-center gap-2 mb-3">
              <span v-if="brandingDetection.state === 'detected'" class="badge bg-success">
                <i class="bi bi-check-circle me-1"></i>Template detected
              </span>
              <span v-else-if="brandingDetection.state === 'uncertain'" class="badge" style="background-color: #6f42c1;">
                <i class="bi bi-question-circle me-1"></i>Could not fully verify
              </span>
              <span class="text-muted small branding-preview-file-name">{{ uploadedFileName }}</span>
            </div>

            <!-- Preview progress steps -->
            <div class="branding-steps mb-3">
              <!-- Step 1: Template verified -->
              <div class="branding-step d-flex align-items-center gap-2 mb-2">
                <i class="bi bi-check-circle-fill text-success"></i>
                <span class="small">Template verified</span>
              </div>

              <!-- Step 2: Server-side branded preview (no Storage write until Save) -->
              <div class="branding-step d-flex align-items-center gap-2 mb-2">
                <template v-if="brandingUploadStep === 'previewing'">
                  <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
                  <span class="small text-primary fw-medium">Generating branded preview...</span>
                </template>
                <template v-else-if="brandingUploadStep === 'ready'">
                  <i class="bi bi-check-circle-fill text-success"></i>
                  <span class="small">Preview ready</span>
                </template>
                <template v-else-if="brandingUploadStep === 'error'">
                  <i class="bi bi-x-circle-fill text-danger"></i>
                  <span class="small text-danger">Preview failed</span>
                </template>
                <template v-else>
                  <i class="bi bi-circle text-muted"></i>
                  <span class="small text-muted">Generate branded preview</span>
                </template>
              </div>

              <!-- Step 3: File rendered / downloaded -->
              <div class="branding-step d-flex align-items-center gap-2 mb-2">
                <template v-if="brandingUploadStep === 'ready'">
                  <i class="bi bi-check-circle-fill text-success"></i>
                  <span class="small">Review file ready</span>
                </template>
                <template v-else-if="brandingUploadStep === 'previewing'">
                  <i class="bi bi-circle text-muted"></i>
                  <span class="small text-muted">Render review file</span>
                </template>
                <template v-else-if="brandingUploadStep === 'error'">
                  <i class="bi bi-x-circle-fill text-danger"></i>
                  <span class="small text-danger">Render failed</span>
                </template>
                <template v-else>
                  <i class="bi bi-circle text-muted"></i>
                  <span class="small text-muted">Render review file</span>
                </template>
              </div>
            </div>

            <!-- Review file download (only when ready) -->
            <div v-if="brandingUploadStep === 'ready'" class="text-center mb-2">
              <button type="button" class="btn btn-outline-primary btn-sm" @click="downloadReviewFile" style="cursor: pointer;">
                <i class="bi bi-download me-1"></i>
                {{ reviewFileButtonLabel }}
              </button>
              <p class="text-muted small mt-2 mb-0">
                Review the generated file, then confirm if you want to save it for future downloads.
              </p>
            </div>

            <!-- Error message -->
            <div v-if="brandingUploadStep === 'error'" class="alert alert-danger small mb-0">
              <i class="bi bi-exclamation-triangle me-1"></i>
              {{ brandingUploadError || 'An error occurred during the upload. Please try again.' }}
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="modal-footer border-0 px-4 pb-4 justify-content-center gap-2">
            <button type="button" class="btn btn-outline-secondary" @click="cancelBrandingPreview" style="cursor: pointer;">
              {{ brandingModalMode === 'unsupported_format' ? 'Remove file' : 'Cancel' }}
            </button>
            <button
              v-if="brandingModalMode === 'preview'"
              type="button"
              class="btn btn-primary"
              @click="acceptBrandingPreview"
              :disabled="brandingUploadStep !== 'ready'"
              style="cursor: pointer;">
              <i class="bi bi-check-lg me-1"></i>
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.content-library-page {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.table-responsive {
  border-radius: 0.375rem;
  overflow: hidden;
}

.content-library-table-shell {
  position: relative;
}

.content-library-table-toolbar {
  max-width: 520px;
}

.content-library-table-scroll {
  overflow-x: auto !important;
  overflow-y: visible;
  max-width: 100%;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
}

.content-library-admin-table {
  min-width: 1500px;
  margin-bottom: 0;
}

.content-library-admin-table td.content-library-col-file-name,
.content-library-admin-table td.content-library-col-category,
.content-library-admin-table td.content-library-col-description,
.content-library-admin-table td.content-library-col-zoom-url {
  overflow: hidden;
  max-width: 0;
}

.table th {
  border-top: none;
  font-weight: 600;
  white-space: nowrap;
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 2;
}

.badge {
  font-size: 0.75em;
}

.list-group-item {
  border-left: none;
  border-right: none;
}

.list-group-item:first-child {
  border-top: none;
}

.list-group-item:last-child {
border-bottom: none;
}

.category-list-copy {
display: flex;
flex-direction: column;
}

.category-list-title {
margin: 0 0 6px;
font-size: 20px;
font-weight: 700;
color: var(--bs-gray-900);
}

.category-list-description {
margin: 0 0 14px;
font-size: 14px;
line-height: 1.5;
max-width: 820px;
}

.category-taxonomy-group-wrap {
display: flex;
align-items: center;
}

.category-taxonomy-group-pill {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
}

.category-taxonomy-group-pill--assigned {
  background: #F5F3FF;
  color: #6f42c1;
  border: 1px solid transparent;
}

.category-taxonomy-group-pill--unassigned {
  background: #F3F4F6;
  color: #6B7280;
  border: 1px solid #D1D5DB;
}

.btn-group .btn {
padding: 0.25rem 0.5rem;
}

.card {
box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.loading-overlay {
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999999;
  height: 100vh;
  width: 100vw;
  --bs-bg-opacity: 0.95;
}

.text-purple {
  color: #6f42c1 !important;
}

/* Modern tab styling - Border Bottom style like Jira/GitHub/Slack */
.nav-tabs .nav-link {
  border: none !important;
  border-bottom: 3px solid transparent !important;
  color: var(--bs-gray-500) !important;
  font-weight: 500;
  transition: all 0.2s ease !important;
  padding: 12px 16px !important;
  margin-bottom: 0 !important;
}

.nav-tabs .nav-link:hover {
  border-bottom-color: var(--bs-purple-light) !important;
  color: var(--bs-gray-600) !important;
  background-color: transparent !important;
}

.nav-tabs .nav-link.active {
  border-bottom-color: var(--bs-purple-intense) !important;
  color: var(--bs-purple-intense) !important;
  background-color: transparent !important;
  font-weight: 600;
}

.nav-tabs {
  border-bottom: 1px solid var(--bs-gray-200) !important;
}

/* Table scroll and spacing improvements */
.table-responsive {
  overflow-x: auto;
  max-width: 100%;
}

.table {
  width: 100%;
  table-layout: fixed;
}

/* Center all table content */
.table th,
.table td {
  text-align: center !important;
  vertical-align: middle !important;
}

/* Center first column specifically */
.table th:first-child,
.table td:first-child {
  text-align: center !important;
  vertical-align: middle !important;
  width: 72px;
  max-width: 72px;
}

.content-library-col-index {
  width: 72px;
  min-width: 72px;
}

.content-library-col-file-name {
  width: 220px;
  min-width: 220px;
}

.content-library-col-category {
  width: 180px;
  min-width: 180px;
}

.content-library-col-short {
  width: 120px;
  min-width: 120px;
}

.content-library-col-cache-status {
  width: 150px;
  min-width: 150px;
}

.content-library-col-description {
  width: 230px;
  min-width: 230px;
}

.content-library-col-zoom-url {
  width: 190px;
  min-width: 190px;
}

.content-library-col-icon {
  width: 100px;
  min-width: 100px;
}

.content-library-cell-truncate {
  display: block;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.content-library-cell-truncate--description {
  max-width: 100%;
}

.content-library-cell-truncate--zoom {
  max-width: 100%;
}

.actions-cell {
  min-width: 160px;
  width: 160px;
  background: #fff;
}

.actions-cell .btn {
  min-width: 32px;
  height: 32px;
  padding: 6px 8px;
}

.content-library-sticky-actions {
  position: sticky;
  right: 0;
  z-index: 3;
  box-shadow: -8px 0 12px rgba(15, 23, 42, 0.06);
}

/* Modal Styles - Matching SentRemindersModal */
.modal.fade.show {
  opacity: 1;
}

.modal-content {
  border: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.modal-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.modal-header .header-22 {
  font-size: 22px;
  font-weight: 600;
}

.modal-body {
  background-color: #ffffff;
}

/* Warning Box Styles */
.warning-box {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.warning-box-clean {
  background-color: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.warning-icon {
  color: #f39c12;
  font-size: 16px;
  margin-top: 2px;
  flex-shrink: 0;
}

.warning-text {
  color: #856404;
  line-height: 1.4;
}

.warning-box-clean .warning-text {
  color: #c53030;
}

.premium-warning-message {
  max-width: 300px;
  margin: 0 auto;
}

.premium-warning-message p {
  margin: 0;
  text-align: center;
}

.modal-warning-wide {
  max-width: 320px; /* modal-sm is 300px, so +20px */
}

.border-light {
  border-color: #dee2e6 !important;
}

.upload-document {
  transition: all 0.3s ease;
  border-style: dashed !important;
  cursor: pointer;
  overflow: hidden;
  min-width: 0;
}

.upload-document-file-name {
  display: block;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
  padding: 0 2.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modal-body .row > [class*='col-'] {
  min-width: 0;
}

.upload-document:hover {
  border-color: #6f42c1 !important;
  background-color: #f8f5ff;
}

.upload-document.dragover {
  border-color: #0d6efd !important;
  border-style: dashed !important;
  background-color: #e7f1ff;
}

.category-select {
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.category-select:focus {
  border-color: #6f42c1 !important;
  box-shadow: 0 0 0 0.2rem rgba(111, 66, 193, 0.25) !important;
}

.category-select option:checked {
  color: #6f42c1 !important;
  font-weight: 600;
}

.clear-category-btn {
  right: 35px;
  top: 50%;
  transform: translateY(-50%);
  background: none !important;
  border: none !important;
  padding: 0 !important;
  color: #6f42c1;
  font-size: 16px;
  cursor: pointer;
  z-index: 10;
}

.clear-category-btn:hover {
  color: #5a2d91;
}

.clear-category-btn:active {
  transform: translateY(-50%) scale(0.95);
}

/* Success/Error Notification - Top Right Corner */
.success-notification {
  position: fixed;
  top: 80px;
  right: 24px;
  z-index: 9999;
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
  color: white;
  padding: 14px 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(16, 185, 129, 0.35);
  display: flex;
  align-items: center;
  font-weight: 500;
  min-width: 220px;
  max-width: 360px;
}

.success-notification.notification-error {
  background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
  box-shadow: 0 4px 20px rgba(239, 68, 68, 0.35);
}

.success-notification i {
  font-size: 18px;
}

/* Notification slide animation */
.slide-notification-enter-active {
  animation: slideIn 0.3s ease-out;
}

.slide-notification-leave-active {
  animation: slideOut 0.3s ease-in;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideOut {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(100px);
  }
}

/* Back to Top Button */
.back-to-top-btn {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6f42c1 0%, #5a2d91 100%);
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(111, 66, 193, 0.4);
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.back-to-top-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(111, 66, 193, 0.5);
}

.back-to-top-btn:active {
  transform: translateY(-1px);
}

/* Fade button animation */
.fade-button-enter-active,
.fade-button-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-button-enter-from,
.fade-button-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Branding Preview Modal */
.branding-preview {
  max-width: 640px;
}

.branding-preview-file-name {
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.branding-steps {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  background: #fafafa;
}

.branding-step i.bi-check-circle-fill {
  font-size: 16px;
}

.branding-step i.bi-x-circle-fill {
  font-size: 16px;
}

.branding-step i.bi-circle {
  font-size: 16px;
}
</style>

<!-- Non-scoped styles for table row highlighting -->
<style>
#contentLibraryTableBody tr td {
  transition: background-color 1s ease-out, font-weight 0.3s ease-out;
}

/* Phase 1: Strong purple + bold (initial highlight) */
#contentLibraryTableBody tr.highlight-row td {
  background-color: #DDD6FE;
  font-weight: 700 !important;
}

/* Phase 2: Softer purple, normal weight */
#contentLibraryTableBody tr.highlight-row.highlight-soft td {
  background-color: #F3E8FF;
  font-weight: normal;
}

/* Phase 3: Fade out to transparent */
#contentLibraryTableBody tr.highlight-row.highlight-fade td {
  background-color: transparent;
  font-weight: normal;
}

/* Category list item highlighting */
.list-group-item {
  transition: background-color 1s ease-out, font-weight 0.3s ease-out;
}

/* Phase 1: Strong purple + bold (initial highlight) */
.list-group-item.highlight-row {
  background-color: #DDD6FE !important;
  font-weight: 700 !important;
}

/* Phase 2: Softer purple, normal weight */
.list-group-item.highlight-row.highlight-soft {
  background-color: #F3E8FF !important;
  font-weight: normal;
}

/* Phase 3: Fade out to transparent */
.list-group-item.highlight-row.highlight-fade {
  background-color: transparent !important;
  font-weight: normal;
}
</style>